import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import {
    FiCheckCircle, FiDownload, FiLock,
    FiRefreshCw, FiSearch, FiUsers, FiActivity,
    FiChevronLeft, FiChevronRight
} from "react-icons/fi";
import ContentWrapper from "../components/contentwrapper/ContentWrapper";
import { fetchCallAllDataByIDAPI } from "../utils/api";

/* ─── helpers ─────────────────────────────────────────────────────────── */
const today   = () => new Date().toISOString().slice(0, 10);
const fmt     = (v) => Number(v || 0).toLocaleString("en-IN");
const clean   = (v, fb = "—") => { const t = String(v ?? "").trim(); return t || fb; };

const dateTime = (v) => {
    const t = String(v || "").trim();
    if (!t) return "—";
    const d = new Date(t.replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return t;
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const displayStatus = (row) =>
    String(row?.agent_remarks || "").trim() ? "Updated" : clean(row?.crm_lead_status, "Pending");

const statusClass = (s) => {
    const v = String(s || "").toLowerCase();
    if (v === "updated")   return "updated";
    if (v === "assigned")  return "assigned";
    if (v === "converted") return "converted";
    if (v === "rejected")  return "rejected";
    return "pending";
};

/* ─── styles ───────────────────────────────────────────────────────────── */
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
    --bg:         #f4f6f9;
    --bg-card:    #ffffff;
    --bg-raised:  #f1f5f9;
    --border:     #e2e8f0;
    --border-2:   #cbd5e1;
    --accent:     #0891b2;
    --accent-lt:  rgba(8,145,178,.08);
    --accent-glow:rgba(8,145,178,.20);
    --text:       #0f172a;
    --text-2:     #475569;
    --text-3:     #94a3b8;
    --danger:     #dc2626;
    --amber:      #d97706;
    --green:      #16a34a;
    --purple:     #7c3aed;
    --blue:       #2563eb;
    --radius:     10px;
    --radius-lg:  14px;
    --shadow-sm:  0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
    --shadow-md:  0 6px 24px rgba(0,0,0,.10);
    --shadow-lg:  0 12px 40px rgba(0,0,0,.14);
}

.hl-root {
    font-family: 'Inter', sans-serif;
    min-height: 100vh;
    color: var(--text);
}

/* ── hero ── */
.hl-hero {
    display: flex; align-items: flex-end; justify-content: space-between;
    gap: 16px; margin-bottom: 28px; flex-wrap: wrap;
}
.hl-hero-eyebrow {
    font-size: 11px; font-weight: 600; letter-spacing: .12em;
    text-transform: uppercase; color: var(--accent); margin-bottom: 6px;
}
.hl-hero h1 {
    font-family: 'DM Sans', sans-serif; font-size: 26px;
    font-weight: 700; color: var(--text); line-height: 1.15;
}
.hl-hero p { font-size: 13px; color: var(--text-2); margin-top: 5px; }
.hl-hero-actions { display: flex; gap: 10px; }

/* ── buttons ── */
.hl-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 9px 18px; border-radius: var(--radius);
    font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600;
    cursor: pointer; border: none; transition: all .16s ease; white-space: nowrap;
}
.hl-btn:disabled { opacity: .45; cursor: not-allowed; }
.hl-btn-primary  { background: var(--accent); color: #fff; }
.hl-btn-primary:not(:disabled):hover { background: #0e7490; box-shadow: 0 4px 14px var(--accent-glow); }
.hl-btn-ghost    { background: var(--bg-card); color: var(--text-2); border: 1px solid var(--border); box-shadow: var(--shadow-sm); }
.hl-btn-ghost:not(:disabled):hover { border-color: var(--accent); color: var(--accent); }
.hl-btn-outline  { background: var(--accent-lt); color: var(--accent); border: 1px solid var(--accent); }
.hl-btn-outline:not(:disabled):hover { background: rgba(8,145,178,.14); }
.hl-btn-reset    { background: transparent; color: var(--text-2); border: 1px solid var(--border); }
.hl-btn-reset:hover { color: var(--danger); border-color: var(--danger); }

/* ══════════════════════════════════════════════════════
   PREMIUM STAT CARDS
   ══════════════════════════════════════════════════════ */
.hl-stats {
    display: grid; grid-template-columns: repeat(4,1fr);
    gap: 16px; margin-bottom: 24px;
}
@media (max-width: 900px) { .hl-stats { grid-template-columns: repeat(2,1fr); } }
@media (max-width: 520px)  { .hl-stats { grid-template-columns: 1fr; } }

.hl-stat {
    border-radius: 18px;
    padding: 22px 24px 20px;
    position: relative;
    overflow: hidden;
    cursor: default;
    transition: transform .20s cubic-bezier(.34,1.56,.64,1), box-shadow .20s ease;
    /* default — overridden per tone */
    background: #3b82f6;
}
.hl-stat:hover {
    transform: translateY(-5px) scale(1.015);
    box-shadow: var(--shadow-lg);
}

/* Per-tone gradient backgrounds */
.hl-stat.blue   { background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); box-shadow: 0 8px 28px rgba(59,130,246,.35); }
.hl-stat.orange { background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); box-shadow: 0 8px 28px rgba(249,115,22,.35); }
.hl-stat.green  { background: linear-gradient(135deg, #22c55e 0%, #15803d 100%); box-shadow: 0 8px 28px rgba(34,197,94,.35); }
.hl-stat.purple { background: linear-gradient(135deg, #a855f7 0%, #7e22ce 100%); box-shadow: 0 8px 28px rgba(168,85,247,.35); }

.hl-stat:hover.blue   { box-shadow: 0 16px 40px rgba(59,130,246,.45); }
.hl-stat:hover.orange { box-shadow: 0 16px 40px rgba(249,115,22,.45); }
.hl-stat:hover.green  { box-shadow: 0 16px 40px rgba(34,197,94,.45); }
.hl-stat:hover.purple { box-shadow: 0 16px 40px rgba(168,85,247,.45); }

/* top-right decorative glow orb */
.hl-stat::before {
    content: '';
    position: absolute;
    top: -28px; right: -28px;
    width: 100px; height: 100px;
    border-radius: 50%;
    background: rgba(255,255,255,.12);
    pointer-events: none;
}
/* bottom-left smaller orb */
.hl-stat::after {
    content: '';
    position: absolute;
    bottom: -20px; left: -20px;
    width: 70px; height: 70px;
    border-radius: 50%;
    background: rgba(255,255,255,.07);
    pointer-events: none;
}

/* icon box — frosted glass pill */
.hl-stat-icon-wrap {
    display: inline-flex; align-items: center; justify-content: center;
    width: 42px; height: 42px; border-radius: 12px;
    background: rgba(255,255,255,.22);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,.30);
    font-size: 18px; color: #fff;
    margin-bottom: 16px;
    position: relative; z-index: 1;
    box-shadow: 0 2px 8px rgba(0,0,0,.10);
}

.hl-stat-label {
    font-size: 11px; font-weight: 700; letter-spacing: .10em;
    text-transform: uppercase; color: rgba(255,255,255,.75);
    margin-bottom: 5px; position: relative; z-index: 1;
}
.hl-stat-value {
    font-family: 'DM Sans', sans-serif; font-size: 32px; font-weight: 800;
    color: #fff; line-height: 1; position: relative; z-index: 1;
    text-shadow: 0 2px 8px rgba(0,0,0,.15);
}

/* shine sweep on hover */
.hl-stat-shine {
    position: absolute;
    top: 0; left: -75%;
    width: 50%; height: 100%;
    background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,.18) 50%, transparent 100%);
    transform: skewX(-20deg);
    transition: left .50s ease;
    pointer-events: none;
    z-index: 2;
}
.hl-stat:hover .hl-stat-shine { left: 130%; }

/* ── filters ── */
.hl-filters {
    background: rgba(255,255,255,.90); backdrop-filter: blur(12px);
    border: 1px solid var(--border); border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm); padding: 16px 20px;
    display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; margin-bottom: 20px;
}
.hl-filter-group { display: flex; flex-direction: column; gap: 5px; }
.hl-filter-group label { font-size: 11px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--text-3); }
.hl-filter-group input,
.hl-filter-group select {
    background: var(--bg-raised); border: 1px solid var(--border);
    border-radius: var(--radius); color: var(--text);
    font-family: 'Inter', sans-serif; font-size: 13px; padding: 8px 12px;
    outline: none; transition: border-color .15s, box-shadow .15s; min-width: 140px;
}
.hl-filter-group input:focus,
.hl-filter-group select:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-lt); }
.hl-filter-search { flex: 1; min-width: 200px; }
.hl-filter-actions { display: flex; gap: 8px; align-items: flex-end; margin-left: auto; }

/* ── table card ── */
.hl-table-card {
    background: var(--bg-card); border: 1px solid var(--border);
    border-radius: var(--radius-lg); box-shadow: var(--shadow-sm);
    overflow: hidden; margin-bottom: 60px;
}
.hl-table-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 16px 22px; border-bottom: 1px solid var(--border);
    flex-wrap: wrap; gap: 12px;
}
.hl-table-title { font-family: 'DM Sans', sans-serif; font-size: 15px; font-weight: 700; color: var(--text); }
.hl-table-count {
    font-size: 12px; color: var(--text-2); background: var(--bg-raised);
    border: 1px solid var(--border); border-radius: 20px; padding: 2px 10px; margin-left: 8px;
}
.hl-table-meta { display: flex; align-items: center; gap: 12px; }
.hl-table-meta p { font-size: 12px; color: var(--text-2); }
.hl-table-meta strong { color: var(--text); }
.hl-table-meta select {
    background: var(--bg-raised); border: 1px solid var(--border); color: var(--text);
    font-size: 12px; font-family: 'Inter', sans-serif; padding: 6px 10px;
    border-radius: 8px; outline: none; cursor: pointer;
}
.hl-table-meta select:focus { border-color: var(--accent); }

/* ── table ── */
.hl-table-wrap { overflow-x: auto; }
.hl-table { width: 100%; border-collapse: collapse; font-size: 13px; }

.hl-table thead tr { background: rgb(15,23,42); }
.hl-table thead th {
    padding: 11px 16px; text-align: left; font-size: 10.5px; font-weight: 700;
    letter-spacing: .09em; text-transform: uppercase; color: #ffffff;
    white-space: nowrap; border-bottom: 2px solid rgb(249, 115, 22);
}
.hl-table thead th:first-child { padding-left: 22px; }
.hl-table thead th:last-child  { padding-right: 22px; }

.hl-table tbody tr { border-bottom: 1px solid var(--border); transition: background .10s; }
.hl-table tbody tr:last-child { border-bottom: none; }
.hl-table tbody tr:hover { background: rgba(59,130,246,.03); }

.hl-table tbody tr.is-skeleton td span {
    display: block; height: 13px; border-radius: 4px;
    background: linear-gradient(90deg, var(--bg-raised) 25%, var(--border) 50%, var(--bg-raised) 75%);
    background-size: 200% 100%; animation: shimmer 1.5s infinite;
}
@keyframes shimmer { from { background-position: 200% 0 } to { background-position: -200% 0 } }

.hl-table tbody td { padding: 12px 16px; color: var(--text); vertical-align: middle; white-space: nowrap; }
.hl-table tbody td:first-child { padding-left: 22px; color: var(--text-3); font-size: 12px; }
.hl-table tbody td:last-child  { padding-right: 22px; }
.hl-table .td-datetime { color: var(--text-2); font-size: 12px; }
.hl-table .td-name strong { font-weight: 600; color: var(--text); }

.hl-mobile { font-family: 'Inter', monospace; font-size: 12px; color: var(--accent); letter-spacing: .03em; }
.hl-campaign-id {
    font-size: 11px; font-weight: 600; color: var(--text-2);
    font-family: 'Inter', monospace; background: var(--bg-raised);
    border: 1px solid var(--border); border-radius: 5px; padding: 2px 7px;
}
.hl-chip {
    display: inline-block; font-size: 11px; font-weight: 600; padding: 3px 9px;
    border-radius: 20px; background: var(--bg-raised); border: 1px solid var(--border); color: var(--text-2);
}
.hl-status {
    display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700;
    padding: 3px 10px; border-radius: 20px; letter-spacing: .04em; text-transform: uppercase;
}
.hl-status.updated   { background: rgba(22,163,74,.08);  color: var(--green);  border: 1px solid rgba(22,163,74,.20); }
.hl-status.pending   { background: rgba(217,119,6,.08);  color: var(--amber);  border: 1px solid rgba(217,119,6,.20); }
.hl-status.assigned  { background: rgba(37,99,235,.08);  color: var(--blue);   border: 1px solid rgba(37,99,235,.20); }
.hl-status.converted { background: rgba(8,145,178,.08);  color: var(--accent); border: 1px solid rgba(8,145,178,.20); }
.hl-status.rejected  { background: rgba(220,38,38,.08);  color: var(--danger); border: 1px solid rgba(220,38,38,.20); }

.hl-remarks { color: var(--text-2); font-size: 12px; max-width: 200px; overflow: hidden; text-overflow: ellipsis; }
.hl-empty td { text-align: center; color: var(--text-3); padding: 56px 16px !important; font-size: 14px; }

/* ── pagination ── */
.hl-pagination {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 22px; border-top: 1px solid var(--border);
    flex-wrap: wrap; gap: 12px; background: var(--bg-raised);
}
.hl-pagination-info { font-size: 12px; color: var(--text-2); }
.hl-pagination-info strong { color: var(--text); }
.hl-pagination-info em { margin-left: 10px; font-style: normal; color: var(--text-3); }
.hl-pagination-pages { display: flex; align-items: center; gap: 4px; }
.hl-page-btn {
    width: 32px; height: 32px; border-radius: 8px; background: var(--bg-card);
    border: 1px solid var(--border); color: var(--text-2); font-size: 12px;
    font-family: 'Inter', sans-serif; font-weight: 600; cursor: pointer;
    display: flex; align-items: center; justify-content: center; transition: all .13s;
}
.hl-page-btn:not(:disabled):hover { border-color: var(--accent); color: var(--accent); background: var(--accent-lt); }
.hl-page-btn.active { background: var(--accent); color: #fff; border-color: var(--accent); }
.hl-page-btn:disabled { opacity: .35; cursor: not-allowed; }
.hl-page-dots { color: var(--text-3); font-size: 12px; padding: 0 4px; }

/* ── locked ── */
.hl-locked {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 80px 24px; text-align: center; background: var(--bg-card);
    border: 1px solid var(--border); border-radius: var(--radius-lg);
    margin-top: 24px; box-shadow: var(--shadow-sm);
}
.hl-locked-icon {
    width: 60px; height: 60px; border-radius: 16px;
    background: rgba(220,38,38,.06); border: 1px solid rgba(220,38,38,.15);
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; color: var(--danger); margin-bottom: 20px;
}
.hl-locked h2 { font-family: 'DM Sans', sans-serif; font-size: 20px; font-weight: 700; margin-bottom: 8px; color: var(--text); }
.hl-locked p  { font-size: 14px; color: var(--text-2); }

@keyframes spin { to { transform: rotate(360deg); } }
.is-spinning { animation: spin .7s linear infinite; }
`;

/* ═══════════════════════════════════════════════════════════════════════ */
const HospitalLeads = () => {
    const ppcid = localStorage.getItem("ppcdata");
    const [rows, setRows]               = useState([]);
    const [summary, setSummary]         = useState({ total: 0, pending: 0, remarks: 0, assigned: 0, campaigns: 0 });
    const [campaignIds, setCampaignIds] = useState([]);
    const [pagination, setPagination]   = useState({ page: 1, per_page: 300, total: 0, total_pages: 0 });
    const [hasAccess, setHasAccess]     = useState(true);
    const [isLoading, setIsLoading]     = useState(false);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate]     = useState("");
    const [search, setSearch]     = useState("");
    const [status, setStatus]     = useState("");
    const [page, setPage]         = useState(1);
    const [perPage, setPerPage]   = useState(300);
    const [applied, setApplied]   = useState({ fromDate: "", toDate: "", search: "", status: "", page: 1, perPage: 300 });

    const fetchLeads = async (filters = applied, silent = false) => {
        if (!ppcid) return;
        setIsLoading(true);
        try {
            const qs = new URLSearchParams();
            if (filters.fromDate) qs.set("start_date", filters.fromDate);
            if (filters.toDate)   qs.set("end_date", filters.toDate);
            if (filters.search)   qs.set("search", filters.search);
            if (filters.status)   qs.set("status", filters.status);
            qs.set("page", filters.page || 1);
            qs.set("per_page", filters.perPage || 300);

            const res = await fetchCallAllDataByIDAPI(`ppc-hospital-leads/${ppcid}?${qs.toString()}`);
            if (res?.data?.status === 1) {
                const payload = res.data.data || {};
                setHasAccess(payload.has_access !== false);
                setRows(Array.isArray(payload.rows) ? payload.rows : []);
                setSummary(payload.summary || { total: 0, pending: 0, remarks: 0, assigned: 0, campaigns: 0 });
                setPagination(payload.pagination || { page: 1, per_page: 300, total: 0, total_pages: 0 });
                setCampaignIds(Array.isArray(payload.campaign_ids) ? payload.campaign_ids : []);
                if (!silent) toast.success("Leads refreshed");
            } else {
                toast.error(res?.data?.message || "Unable to load leads");
                setRows([]);
            }
        } catch (err) {
            console.error(err);
            toast.error("Network error while loading leads");
            setRows([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLeads({ fromDate: "", toDate: "", search: "", status: "", page: 1, perPage: 300 }, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    const applyFilters = () => {
        const next = { fromDate, toDate, search: search.trim(), status, page: 1, perPage };
        setPage(1); setApplied(next); fetchLeads(next);
    };

    const resetFilters = () => {
        const next = { fromDate: "", toDate: "", search: "", status: "", page: 1, perPage: 300 };
        setFromDate(""); setToDate(""); setSearch(""); setStatus("");
        setPage(1); setPerPage(300); setApplied(next); fetchLeads(next);
    };

    const goToPage = (nextPage) => {
        const totalPages = Number(pagination.total_pages || 0);
        const safePage = Math.min(Math.max(1, Number(nextPage || 1)), totalPages || 1);
        setPage(safePage);
        const next = { ...applied, page: safePage, perPage };
        setApplied(next); fetchLeads(next, true);
    };

    const changePerPage = (value) => {
        const next = { ...applied, page: 1, perPage: Number(value || 300) };
        setPerPage(Number(value || 300)); setPage(1); setApplied(next); fetchLeads(next, true);
    };

    const pageNumbers = useMemo(() => {
        const totalPages = Number(pagination.total_pages || 0);
        const current    = Number(pagination.page || page || 1);
        if (totalPages <= 0) return [1];
        if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
        const pages = new Set([1, totalPages, current, current - 1, current + 1]);
        return Array.from(pages)
            .filter(p => p >= 1 && p <= totalPages)
            .sort((a, b) => a - b)
            .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
            }, []);
    }, [pagination.page, pagination.total_pages, page]);

    const exportRows = () => {
        if (!rows.length) { toast.info("No leads to export"); return; }
        const headers = ["Created","Name","Mobile","Campaign ID","Campaign","Form","Issue","Age","Status","Agent Remarks"];
        const csvRows = rows.map(r => [
            clean(r.created_time || r.system_created_at, ""),
            clean(r.full_name,""), clean(r.phone_number,""), clean(r.campaign_id,""),
            clean(r.campaign_name,""), clean(r.form_name,""), clean(r.liver_related_issue,""),
            clean(r.age_group,""), displayStatus(r), clean(r.agent_remarks,""),
        ]);
        const csv = [headers, ...csvRows].map(l => l.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url  = URL.createObjectURL(blob);
        const a    = Object.assign(document.createElement("a"), { href: url, download: `hospital-leads-${today()}.csv` });
        a.click(); URL.revokeObjectURL(url);
    };

    const statCards = useMemo(() => [
        { label: "Total Leads",   value: summary.total,      icon: <FiUsers />,       tone: "blue"   },
        { label: "Agent Remarks", value: summary.remarks,    icon: <FiActivity />,    tone: "orange" },
        { label: "Assigned",      value: summary.assigned,   icon: <FiCheckCircle />, tone: "green"  },
        { label: "Campaigns",     value: campaignIds.length, icon: <FiSearch />,      tone: "purple" },
    ], [summary, campaignIds.length]);

    const currentPage = Number(pagination.page || page || 1);

    return (
        <>
            <Helmet><title>PPC Hospital Leads</title></Helmet>
            <style>{STYLE}</style>
            <ContentWrapper>
                <div className="hl-root">

                    {/* hero */}
                    <header className="hl-hero">
                        <div>
                            <div className="hl-hero-eyebrow">Hospital Ads · PPC</div>
                            <h1>Hospital Leads</h1>
                            <p>Campaign-wise leads for your PPC account</p>
                        </div>
                        <div className="hl-hero-actions">
                            <button className="hl-btn hl-btn-ghost" onClick={() => fetchLeads(applied)} disabled={isLoading}>
                                <FiRefreshCw className={isLoading ? "is-spinning" : ""} />
                                {isLoading ? "Loading…" : "Refresh"}
                            </button>
                            <button className="hl-btn hl-btn-primary" onClick={exportRows}>
                                <FiDownload /> Export CSV
                            </button>
                        </div>
                    </header>

                    {!hasAccess ? (
                        <div className="hl-locked">
                            <div className="hl-locked-icon"><FiLock /></div>
                            <h2>No Hospital Lead Access</h2>
                            <p>Your PPC profile does not have hospital campaign IDs mapped.</p>
                        </div>
                    ) : (
                        <>
                            {/* ── PREMIUM STAT CARDS ── */}
                            <section className="hl-stats">
                                {statCards.map(card => (
                                    <article className={`hl-stat ${card.tone}`} key={card.label}>
                                        {/* shine sweep element */}
                                        <div className="hl-stat-shine" />
                                        {/* frosted icon */}
                                        <div className="hl-stat-icon-wrap">{card.icon}</div>
                                        <div className="hl-stat-label">{card.label}</div>
                                        <div className="hl-stat-value">{fmt(card.value)}</div>
                                    </article>
                                ))}
                            </section>

                            {/* filters */}
                            <div className="hl-filters">
                                <div className="hl-filter-group">
                                    <label>From Date</label>
                                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                                </div>
                                <div className="hl-filter-group">
                                    <label>To Date</label>
                                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                                </div>
                                <div className="hl-filter-group">
                                    <label>Status</label>
                                    <select value={status} onChange={e => setStatus(e.target.value)}>
                                        <option value="">All Status</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Assigned">Assigned</option>
                                        <option value="Converted">Converted</option>
                                        <option value="Rejected">Rejected</option>
                                    </select>
                                </div>
                                <div className="hl-filter-group hl-filter-search">
                                    <label>Search</label>
                                    <input
                                        type="search" value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        placeholder="Name, mobile, campaign…"
                                        onKeyDown={e => e.key === "Enter" && applyFilters()}
                                    />
                                </div>
                                <div className="hl-filter-actions">
                                    <button className="hl-btn hl-btn-outline" onClick={applyFilters}>Apply</button>
                                    <button className="hl-btn hl-btn-reset"   onClick={resetFilters}>Reset</button>
                                </div>
                            </div>

                            {/* table */}
                            <div className="hl-table-card">
                                <div className="hl-table-head">
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span className="hl-table-title">Hospital Lead Records</span>
                                        <span className="hl-table-count">{fmt(pagination.total || rows.length)} total</span>
                                    </div>
                                    <div className="hl-table-meta">
                                        <p>Showing <strong>{fmt(rows.length)}</strong> of <strong>{fmt(pagination.total || rows.length)}</strong></p>
                                        <select value={perPage} onChange={e => changePerPage(e.target.value)}>
                                            <option value="100">100 / page</option>
                                            <option value="300">300 / page</option>
                                            <option value="500">500 / page</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="hl-table-wrap">
                                    <table className="hl-table">
                                        <thead>
                                            <tr>
                                                <th>#</th><th>Created</th><th>Name</th><th>Mobile</th>
                                                <th>Campaign</th><th>Form</th><th>Issue</th><th>Age</th>
                                                <th>Status</th><th>Agent Remarks</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {isLoading ? (
                                                Array.from({ length: 8 }).map((_, i) => (
                                                    <tr className="is-skeleton" key={i}>
                                                        {Array.from({ length: 10 }).map((__, c) => <td key={c}><span /></td>)}
                                                    </tr>
                                                ))
                                            ) : rows.length ? (
                                                rows.map((row, idx) => {
                                                    const st = displayStatus(row);
                                                    return (
                                                        <tr key={row.id || row.google_lead_id || `${row.campaign_id}-${row.phone_number}-${idx}`}>
                                                            <td>{((currentPage - 1) * Number(pagination.per_page || perPage || 300)) + idx + 1}</td>
                                                            <td className="td-datetime">{dateTime(row.created_time || row.system_created_at)}</td>
                                                            <td className="td-name"><strong>{clean(row.full_name)}</strong></td>
                                                            <td><span className="hl-mobile">{clean(row.phone_number)}</span></td>
                                                            <td>
                                                                <div style={{ display:"flex", flexDirection:"column", gap: 3 }}>
                                                                    <span className="hl-campaign-id">{clean(row.campaign_id)}</span>
                                                                    <span style={{ fontSize: 11, color: "var(--text-2)" }}>{clean(row.campaign_name)}</span>
                                                                </div>
                                                            </td>
                                                            <td style={{ fontSize: 12, color: "var(--text-2)" }}>{clean(row.form_name)}</td>
                                                            <td style={{ fontSize: 12 }}>{clean(row.liver_related_issue)}</td>
                                                            <td><span className="hl-chip">{clean(row.age_group)}</span></td>
                                                            <td><span className={`hl-status ${statusClass(st)}`}>{st}</span></td>
                                                            <td className="hl-remarks">{clean(row.agent_remarks)}</td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr className="hl-empty"><td colSpan="10">No hospital leads found.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* pagination */}
                                <div className="hl-pagination">
                                    <span className="hl-pagination-info">
                                        Showing <strong>{fmt(rows.length)}</strong> of <strong>{fmt(pagination.total || rows.length)}</strong> leads
                                        <em>· Page <strong>{fmt(currentPage)}</strong> of <strong>{fmt(pagination.total_pages || 1)}</strong></em>
                                    </span>
                                    <div className="hl-pagination-pages">
                                        <button className="hl-page-btn" disabled={isLoading || currentPage <= 1} onClick={() => goToPage(currentPage - 1)} aria-label="Previous">
                                            <FiChevronLeft />
                                        </button>
                                        {pageNumbers.map((item, i) => item === "..." ? (
                                            <span key={`dots-${i}`} className="hl-page-dots">…</span>
                                        ) : (
                                            <button key={item} className={`hl-page-btn ${currentPage === item ? "active" : ""}`} disabled={isLoading} onClick={() => goToPage(item)}>
                                                {item}
                                            </button>
                                        ))}
                                        <button className="hl-page-btn" disabled={isLoading || currentPage >= Number(pagination.total_pages || 1)} onClick={() => goToPage(currentPage + 1)} aria-label="Next">
                                            <FiChevronRight />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </ContentWrapper>
        </>
    );
};

export default HospitalLeads;
